import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type AppTheme = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {

  private readonly STORAGE_KEY = 'op-theme';
  private readonly LEGACY_KEY = 'gantt-theme';

  private readonly themeSubject: BehaviorSubject<AppTheme>;
  theme$: Observable<AppTheme>;

  constructor() {
    const initial = this.resolveInitialTheme();
    this.themeSubject = new BehaviorSubject<AppTheme>(initial);
    this.theme$ = this.themeSubject.asObservable();
    this.applyTheme(initial);
  }

  get currentTheme(): AppTheme {
    return this.themeSubject.value;
  }

  toggle(): void {
    this.setTheme(this.currentTheme === 'light' ? 'dark' : 'light');
  }

  setTheme(theme: AppTheme): void {
    this.themeSubject.next(theme);
    this.applyTheme(theme);
    this.persist(theme);
  }

  private applyTheme(theme: AppTheme): void {
    const isDark = theme === 'dark';
    // Primary target: <html> element — matches FOUC script
    document.documentElement.classList.toggle('op-theme-dark', isDark);
    // Secondary target: <body> — daterangepicker compat
    document.body.classList.toggle('gantt-theme-dark', isDark);
  }

  private persist(theme: AppTheme): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, theme);
    } catch {
      // ignore storage errors
    }
  }

  private resolveInitialTheme(): AppTheme {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved === 'light' || saved === 'dark') {
        return saved;
      }
      // Migrate legacy key
      const legacy = localStorage.getItem(this.LEGACY_KEY);
      if (legacy === 'dark' || legacy === 'light') {
        localStorage.setItem(this.STORAGE_KEY, legacy);
        localStorage.removeItem(this.LEGACY_KEY);
        return legacy;
      }
    } catch {
      // ignore
    }
    return 'light';
  }
}
